import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ChevronRight, UploadCloud, FolderPlus, List, Grid, Download, Edit2, Trash2,
  FolderInput, X, FolderX, Loader2, CalendarClock,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import api from "../../lib/api";
import { cn } from "../../lib/utils";
import { Button, Modal, Input, Field, Segmented, Spinner } from "../ui";
import { Avatar } from "../board/BoardCells";
import FolderTree from "./FolderTree";
import { fileIcon, formatBytes, isPreviewable, expiryMeta, FOLDER_ICON } from "./driveUtils";

const fileUrl = (id) => `${window.location.origin}/api/drive/files/${id}/download`;

function flattenTree(node, depth = 0, out = []) {
  if (!node) return out;
  out.push({ id: node.id, name: node.name, depth, kind: node.kind });
  (node.children || []).forEach((c) => flattenTree(c, depth + 1, out));
  return out;
}

function findNode(node, id) {
  if (!node) return null;
  if (node.id === id) return node;
  for (const c of node.children || []) {
    const r = findNode(c, id);
    if (r) return r;
  }
  return null;
}

const TONE = {
  expired: "bg-red-500/10 text-red-500",
  soon: "bg-amber-500/10 text-amber-600",
  ok: "text-text-secondary",
};

function ExpiryBadge({ value, lang }) {
  const m = expiryMeta(value, lang);
  if (!m) return <span className="text-text-secondary">—</span>;
  return (
    <span className={cn("text-xs px-1.5 py-0.5 rounded-full whitespace-nowrap", TONE[m.tone])}
      title={m.date} data-testid="expiry-badge">{m.label}</span>
  );
}

/**
 * SharePoint-style Drive browser. When `rootId` is an auto-folder id (e.g.
 * `e:<entity_id>`) the view is rooted there: the sidebar tree, breadcrumbs and
 * move targets are all scoped to that subtree — you cannot navigate above it.
 */
export default function DriveBrowser({ rootId = "root", embedded = false }) {
  const { t, lang, toast } = useApp();
  const [tree, setTree] = useState(null);
  const [configured, setConfigured] = useState(true);
  const [usedBytes, setUsedBytes] = useState(0);
  const [limitBytes, setLimitBytes] = useState(null);
  const [currentId, setCurrentId] = useState(rootId);
  const [contents, setContents] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("drive_view") || "list");
  const [selected, setSelected] = useState(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [modal, setModal] = useState(null); // {type, target}
  const [modalName, setModalName] = useState("");
  const [expiryVal, setExpiryVal] = useState("");
  const [moveTarget, setMoveTarget] = useState(rootId);
  const [preview, setPreview] = useState(null);
  const fileInput = useRef(null);

  const loadTree = useCallback(async () => {
    const { data } = await api.get("/drive/tree");
    setTree(data.tree);
    setConfigured(data.configured);
    setUsedBytes(data.used_bytes);
    setLimitBytes(data.limit_bytes ?? null);
  }, []);

  const loadFolder = useCallback(async (id) => {
    setContents(null);
    setSelected(new Set());
    try {
      const { data } = await api.get(`/drive/folders/${id}`);
      setContents(data);
    } catch {
      setCurrentId(rootId);
    }
  }, [rootId]);

  useEffect(() => { loadTree(); }, [loadTree]);
  useEffect(() => { loadFolder(currentId); }, [currentId, loadFolder]);
  useEffect(() => { localStorage.setItem("drive_view", view); }, [view]);

  const refresh = useCallback(() => { loadTree(); loadFolder(currentId); }, [loadTree, loadFolder, currentId]);

  // ---- uploads ----
  const doUpload = useCallback(async (fileList) => {
    if (!configured) { toast(t("drive_not_configured")); return; }
    const files = Array.from(fileList);
    for (const file of files) {
      const uid = Math.random().toString(36).slice(2);
      setUploads((u) => [...u, { uid, name: file.name, status: "uploading" }]);
      const form = new FormData();
      form.append("folder_id", currentId);
      form.append("file", file);
      try {
        await api.post("/drive/files", form);
        setUploads((u) => u.map((x) => (x.uid === uid ? { ...x, status: "done" } : x)));
      } catch (e) {
        const code = e?.response?.status;
        const msg = e?.response?.data?.detail?.message;
        if (code === 413) toast(t("drive_too_large"));
        else if (code === 402 && msg) toast(msg);
        else toast(t("drive_upload_failed"));
        setUploads((u) => u.map((x) => (x.uid === uid ? { ...x, status: "error" } : x)));
      }
    }
    toast(t("drive_uploaded"));
    refresh();
    setTimeout(() => setUploads([]), 1500);
  }, [configured, currentId, refresh, t, toast]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) doUpload(e.dataTransfer.files);
  };

  // ---- folder/file actions ----
  const submitModal = async () => {
    const name = modalName.trim();
    if (modal.type === "newFolder") {
      if (!name) return;
      await api.post("/drive/folders", { name, parent_id: currentId });
      toast(t("drive_folder_created"));
    } else if (modal.type === "renameFolder") {
      await api.patch(`/drive/folders/${modal.target.id}`, { name });
      toast(t("drive_renamed"));
    } else if (modal.type === "renameFile") {
      await api.patch(`/drive/files/${modal.target.id}`, { filename: name });
      toast(t("drive_renamed"));
    }
    setModal(null);
    refresh();
  };

  const submitExpiry = async () => {
    await api.patch(`/drive/files/${modal.target.id}`, { expiry_date: expiryVal });
    toast(lang === "fr" ? "Échéance enregistrée" : "Expiry saved");
    setModal(null);
    refresh();
  };

  const deleteFile = async (f) => {
    await api.delete(`/drive/files/${f.id}`);
    toast(t("drive_deleted"));
    refresh();
  };
  const deleteFolder = async (folder) => {
    await api.delete(`/drive/folders/${folder.id}`);
    toast(t("drive_deleted"));
    if (currentId === folder.id) setCurrentId(rootId);
    else refresh();
  };
  const bulkDelete = async () => {
    await api.post("/drive/files/bulk", { action: "delete", ids: Array.from(selected) });
    toast(t("drive_deleted"));
    refresh();
  };
  const submitMove = async () => {
    await api.post("/drive/files/bulk", { action: "move", ids: Array.from(selected), folder_id: moveTarget });
    toast(t("drive_moved"));
    setModal(null);
    refresh();
  };

  const openFile = (f) => {
    if (isPreviewable(f.filename, f.content_type)) setPreview(f);
    else window.open(fileUrl(f.id), "_blank");
  };
  const download = (f) => {
    const a = document.createElement("a");
    a.href = fileUrl(f.id);
    a.download = f.filename;
    a.click();
  };
  const toggleSel = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const openModal = (type, target = null) => {
    setModal({ type, target });
    setModalName(type === "renameFolder" ? target?.name : type === "renameFile" ? target?.filename : "");
  };
  const openExpiry = (file) => {
    setModal({ type: "expiry", target: file });
    setExpiryVal(file.expiry_date || "");
  };

  const folders = contents?.subfolders || [];
  const files = contents?.files || [];
  const displayTree = rootId === "root" ? tree : findNode(tree, rootId);
  const flatFolders = flattenTree(displayTree);
  const crumbs = (() => {
    const cr = contents?.breadcrumbs || [{ id: rootId, name: t("drive_root") }];
    if (rootId === "root") return cr;
    const idx = cr.findIndex((b) => b.id === rootId);
    return idx >= 0 ? cr.slice(idx) : cr;
  })();

  const cellProps = { folders, files, currentId, setCurrentId, selected, toggleSel, openFile, download, openModal, openExpiry, deleteFile, deleteFolder, t, lang };

  return (
    <div
      className={cn("flex flex-col md:flex-row overflow-hidden bg-background",
        embedded ? "h-[72vh] rounded-2xl border" : "h-full")}
      data-testid={embedded ? "entity-documents" : "documents-page"}
    >
      <FolderTree tree={displayTree} currentId={currentId} onSelect={setCurrentId}
        usedBytes={usedBytes} limitBytes={limitBytes} t={t} rootId={rootId} />

      <div
        className="flex-1 flex flex-col min-w-0 bg-background relative"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
        onDrop={onDrop}
        data-testid="upload-drop-zone"
      >
        {/* header */}
        <div className="sticky top-0 z-10 bg-surface/80 backdrop-blur-md border-b p-4 flex items-center gap-3 flex-wrap">
          <nav className="flex items-center gap-1 text-sm min-w-0 flex-1 overflow-x-auto">
            {crumbs.map((b, i, arr) => (
              <span key={b.id} className="flex items-center gap-1 shrink-0">
                {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-text-secondary" />}
                <button
                  onClick={() => setCurrentId(b.id)}
                  data-testid={`breadcrumb-${b.id}`}
                  className={cn("px-1.5 py-0.5 rounded hover:bg-surface-hover transition-colors truncate max-w-[180px]",
                    i === arr.length - 1 ? "text-text-primary font-medium" : "text-text-secondary")}
                >{b.id === "root" ? t("drive_root") : b.name}</button>
              </span>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Segmented testid="view-toggle" value={view} onChange={setView}
              options={[{ value: "list", label: <List className="w-4 h-4" /> }, { value: "grid", label: <Grid className="w-4 h-4" /> }]} />
            <Button variant="secondary" size="sm" data-testid="btn-new-folder" onClick={() => openModal("newFolder")}>
              <FolderPlus className="w-4 h-4 mr-1.5" />{t("drive_new_folder")}
            </Button>
            <Button size="sm" data-testid="btn-upload" onClick={() => fileInput.current?.click()} disabled={!configured}>
              <UploadCloud className="w-4 h-4 mr-1.5" />{t("drive_upload")}
            </Button>
            <input ref={fileInput} type="file" multiple className="hidden"
              onChange={(e) => { if (e.target.files?.length) doUpload(e.target.files); e.target.value = ""; }} />
          </div>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto">
          {!configured && (
            <div className="flex flex-col items-center justify-center text-center py-24 px-6" data-testid="drive-not-configured">
              <UploadCloud className="w-10 h-10 text-text-secondary mb-3" />
              <h3 className="font-heading text-lg text-text-primary">{t("drive_not_configured_title")}</h3>
              <p className="text-sm text-text-secondary max-w-md mt-1">{t("drive_not_configured")}</p>
            </div>
          )}
          {configured && contents === null && <div className="py-24 flex justify-center"><Spinner className="w-7 h-7 text-text-secondary" /></div>}
          {configured && contents && folders.length === 0 && files.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center py-24 px-6" data-testid="drive-empty">
              <FolderX className="w-10 h-10 text-text-secondary mb-3" />
              <p className="text-sm text-text-secondary">{t("drive_empty")}</p>
              {configured && <Button size="sm" className="mt-4" onClick={() => fileInput.current?.click()}><UploadCloud className="w-4 h-4 mr-1.5" />{t("drive_upload")}</Button>}
            </div>
          )}

          {configured && contents && (folders.length > 0 || files.length > 0) && (
            view === "list" ? <ListView {...cellProps} /> : <GridView {...cellProps} />
          )}
        </div>

        {/* drag overlay */}
        {dragOver && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-surface/80 backdrop-blur-sm border-2 border-dashed border-primary m-3 rounded-2xl animate-fade-in pointer-events-none">
            <div className="text-center"><UploadCloud className="w-10 h-10 text-primary mx-auto mb-2" /><p className="font-medium text-text-primary">{t("drive_drop_here")}</p></div>
          </div>
        )}

        {/* bulk bar */}
        {selected.size > 0 && (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 rounded-full bg-surface border shadow-xl px-4 py-2 animate-fade-up" data-testid="bulk-action-bar">
            <span className="text-sm font-medium text-text-primary">{selected.size} {t("drive_selected")}</span>
            <div className="w-px h-5 bg-border" />
            <button data-testid="bulk-action-move" onClick={() => { setMoveTarget(rootId); setModal({ type: "move" }); }} className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"><FolderInput className="w-4 h-4" />{t("drive_move")}</button>
            <button data-testid="bulk-action-delete" onClick={bulkDelete} className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" />{t("drive_delete")}</button>
            <button onClick={() => setSelected(new Set())} className="text-text-secondary hover:text-text-primary"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* upload progress panel */}
        {uploads.length > 0 && (
          <div className="fixed bottom-6 right-6 z-30 w-72 rounded-xl bg-surface border shadow-xl overflow-hidden" data-testid="upload-progress">
            {uploads.map((u) => (
              <div key={u.uid} className="flex items-center gap-2 px-3 py-2 text-sm border-b last:border-b-0">
                {u.status === "uploading" ? <Loader2 className="w-4 h-4 spin text-primary" /> : u.status === "done" ? <span className="w-4 h-4 text-green-500">✓</span> : <X className="w-4 h-4 text-red-500" />}
                <span className="truncate flex-1 text-text-primary">{u.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* create / rename modal */}
      <Modal open={!!modal && ["newFolder", "renameFolder", "renameFile"].includes(modal.type)} onClose={() => setModal(null)}
        title={modal?.type === "newFolder" ? t("drive_new_folder") : t("drive_rename")}
        footer={<>
          <Button variant="secondary" onClick={() => setModal(null)}>{t("drive_cancel")}</Button>
          <Button onClick={submitModal} data-testid="modal-submit">{modal?.type === "newFolder" ? t("drive_create") : t("drive_save")}</Button>
        </>}>
        <Field label={modal?.type === "newFolder" ? t("drive_folder_name") : t("drive_col_name")}>
          <Input autoFocus value={modalName} onChange={(e) => setModalName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitModal()} data-testid="modal-name-input" />
        </Field>
      </Modal>

      {/* expiry modal */}
      <Modal open={modal?.type === "expiry"} onClose={() => setModal(null)} title={t("drive_expiry")}
        footer={<>
          {expiryVal && <Button variant="secondary" onClick={() => setExpiryVal("")} data-testid="expiry-clear">{t("drive_clear_expiry")}</Button>}
          <Button variant="secondary" onClick={() => setModal(null)}>{t("drive_cancel")}</Button>
          <Button onClick={submitExpiry} data-testid="expiry-submit">{t("drive_save")}</Button>
        </>}>
        <p className="text-xs text-text-secondary mb-3">{t("drive_expiry_hint")}</p>
        <Field label={t("drive_col_expiry")}>
          <Input type="date" autoFocus value={expiryVal || ""} onChange={(e) => setExpiryVal(e.target.value)}
            data-testid="expiry-date-input" />
        </Field>
      </Modal>

      {/* move modal */}
      <Modal open={modal?.type === "move"} onClose={() => setModal(null)} title={t("drive_move")}
        footer={<>
          <Button variant="secondary" onClick={() => setModal(null)}>{t("drive_cancel")}</Button>
          <Button onClick={submitMove} data-testid="move-submit">{t("drive_move_here")}</Button>
        </>}>
        <div className="max-h-72 overflow-y-auto -mx-1">
          {flatFolders.map((f) => {
            const Icon = FOLDER_ICON[f.kind] || FOLDER_ICON.manual;
            return (
              <button key={f.id} onClick={() => setMoveTarget(f.id)} data-testid={`move-target-${f.id}`}
                style={{ paddingLeft: 8 + f.depth * 14 }}
                className={cn("w-full flex items-center gap-2 pr-2 py-1.5 rounded-lg text-sm text-left transition-colors",
                  moveTarget === f.id ? "bg-primary/10 text-primary font-medium" : "text-text-secondary hover:bg-surface-hover")}>
                <Icon className="w-4 h-4 shrink-0" /><span className="truncate">{f.id === "root" ? t("drive_root") : f.name}</span>
              </button>
            );
          })}
        </div>
      </Modal>

      {/* preview modal */}
      <Modal open={!!preview} onClose={() => setPreview(null)} title={preview?.filename}>
        {preview && (preview.content_type === "application/pdf" || (preview.filename || "").toLowerCase().endsWith(".pdf") ? (
          <iframe title={preview.filename} src={fileUrl(preview.id)} className="w-full h-[70vh] rounded-lg border" />
        ) : (
          <img alt={preview.filename} src={fileUrl(preview.id)} className="max-w-full max-h-[70vh] mx-auto rounded-lg" />
        ))}
      </Modal>
    </div>
  );
}

// ---- List view ----
function ListView({ folders, files, setCurrentId, selected, toggleSel, openFile, download, openModal, openExpiry, deleteFile, deleteFolder, t, lang }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-text-secondary border-b">
        <tr className="text-left">
          <th className="w-10" />
          <th className="font-medium py-2 px-3">{t("drive_col_name")}</th>
          <th className="font-medium py-2 px-3 hidden sm:table-cell">{t("drive_col_expiry")}</th>
          <th className="font-medium py-2 px-3 hidden md:table-cell">{t("drive_col_size")}</th>
          <th className="font-medium py-2 px-3 hidden lg:table-cell">{t("drive_col_owner")}</th>
          <th className="w-32" />
        </tr>
      </thead>
      <tbody>
        {folders.map((f) => {
          const Icon = FOLDER_ICON[f.kind] || FOLDER_ICON.manual;
          return (
            <tr key={f.id} className="group border-b border-border/50 hover:bg-surface-hover transition-colors cursor-pointer"
              data-testid={`folder-row-${f.id}`} onClick={() => setCurrentId(f.id)}>
              <td />
              <td className="py-2.5 px-3"><div className="flex items-center gap-2.5"><Icon className={cn("w-4 h-4", f.kind === "manual" ? "text-text-secondary" : "text-primary")} /><span className="text-text-primary font-medium truncate">{f.name}</span></div></td>
              <td className="py-2.5 px-3 hidden sm:table-cell text-text-secondary">—</td>
              <td className="py-2.5 px-3 hidden md:table-cell text-text-secondary">—</td>
              <td className="py-2.5 px-3 hidden lg:table-cell" />
              <td className="py-2.5 px-3 text-right">
                {f.kind === "manual" && (
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <IconBtn testid={`folder-rename-${f.id}`} onClick={(e) => { e.stopPropagation(); openModal("renameFolder", f); }} icon={Edit2} />
                    <IconBtn testid={`folder-delete-${f.id}`} onClick={(e) => { e.stopPropagation(); deleteFolder(f); }} icon={Trash2} danger />
                  </div>
                )}
              </td>
            </tr>
          );
        })}
        {files.map((f) => {
          const Icon = fileIcon(f.filename, f.content_type);
          return (
            <tr key={f.id} className="group border-b border-border/50 hover:bg-surface-hover transition-colors" data-testid={`file-row-${f.id}`}>
              <td className="text-center">
                {!f.readonly && <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggleSel(f.id)} data-testid={`file-select-${f.id}`} className="rounded" />}
              </td>
              <td className="py-2.5 px-3 cursor-pointer" onClick={() => openFile(f)}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <Icon className="w-4 h-4 text-text-secondary shrink-0" />
                  <span className="text-text-primary truncate">{f.filename}</span>
                  {f.readonly && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary shrink-0">{t("drive_from_board")}</span>}
                </div>
              </td>
              <td className="py-2.5 px-3 hidden sm:table-cell">{f.readonly ? <span className="text-text-secondary">—</span> : <ExpiryBadge value={f.expiry_date} lang={lang} />}</td>
              <td className="py-2.5 px-3 hidden md:table-cell text-text-secondary">{formatBytes(f.size)}</td>
              <td className="py-2.5 px-3 hidden lg:table-cell">{f.actor_name && <Avatar person={{ name: f.actor_name, picture: f.actor_picture }} size={22} />}</td>
              <td className="py-2.5 px-3 text-right">
                <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <IconBtn testid={`file-download-${f.id}`} onClick={() => download(f)} icon={Download} />
                  {!f.readonly && <IconBtn testid={`file-expiry-${f.id}`} onClick={() => openExpiry(f)} icon={CalendarClock} title={t("drive_set_expiry")} />}
                  {!f.readonly && <IconBtn testid={`file-rename-${f.id}`} onClick={() => openModal("renameFile", f)} icon={Edit2} />}
                  {!f.readonly && <IconBtn testid={`file-delete-${f.id}`} onClick={() => deleteFile(f)} icon={Trash2} danger />}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---- Grid view ----
function GridView({ folders, files, setCurrentId, selected, toggleSel, openFile, download, openModal, openExpiry, deleteFile, deleteFolder, t, lang }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 p-4">
      {folders.map((f) => {
        const Icon = FOLDER_ICON[f.kind] || FOLDER_ICON.manual;
        return (
          <button key={f.id} onClick={() => setCurrentId(f.id)} data-testid={`file-grid-card-${f.id}`}
            className="group rounded-xl border bg-surface hover:bg-surface-hover hover:border-primary/40 transition-all p-3 flex flex-col items-center text-center">
            <Icon className={cn("w-10 h-10 mb-2", f.kind === "manual" ? "text-text-secondary" : "text-primary")} />
            <span className="text-xs text-text-primary font-medium truncate w-full">{f.name}</span>
          </button>
        );
      })}
      {files.map((f) => {
        const Icon = fileIcon(f.filename, f.content_type);
        const em = f.readonly ? null : expiryMeta(f.expiry_date, lang);
        return (
          <div key={f.id} className="group relative rounded-xl border bg-surface hover:border-primary/40 transition-all overflow-hidden" data-testid={`file-grid-card-${f.id}`}>
            {!f.readonly && <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggleSel(f.id)} data-testid={`file-select-${f.id}`}
              className="absolute top-2 left-2 z-10 rounded opacity-0 group-hover:opacity-100 checked:opacity-100 transition-opacity" />}
            <button onClick={() => openFile(f)} className="w-full aspect-square flex items-center justify-center bg-surface-hover/40">
              <Icon className="w-12 h-12 text-text-secondary" />
            </button>
            <div className="p-2.5 border-t">
              <p className="text-xs text-text-primary truncate font-medium">{f.filename}</p>
              <p className="text-[11px] text-text-secondary">{formatBytes(f.size)}</p>
              {em && em.tone !== "ok" && <span className={cn("inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full", TONE[em.tone])}>{em.label}</span>}
            </div>
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <IconBtn testid={`file-download-${f.id}`} onClick={() => download(f)} icon={Download} />
              {!f.readonly && <IconBtn testid={`file-expiry-${f.id}`} onClick={() => openExpiry(f)} icon={CalendarClock} title={t("drive_set_expiry")} />}
              {!f.readonly && <IconBtn testid={`file-delete-${f.id}`} onClick={() => deleteFile(f)} icon={Trash2} danger />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function IconBtn({ icon: Icon, onClick, danger, testid, title }) {
  return (
    <button onClick={onClick} data-testid={testid} title={title}
      className={cn("w-7 h-7 rounded-lg flex items-center justify-center bg-surface border transition-colors",
        danger ? "text-text-secondary hover:text-red-500 hover:border-red-300" : "text-text-secondary hover:text-text-primary")}>
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}
